import { useQuery } from '@tanstack/react-query';
import { appQueries } from '../../api/app.api';
import { ShowAllAppsAccessMode } from '../../api/app.types';
import { graphQueries } from '../../api/graph.api';

/**
 * Hook to determine if the current user may turn on the "Show all apps" switch.
 * The switch is allowed when:
 * - The user is an app management admin, OR
 * - The admin has configured showAllAppsAccessMode as AllUsers, OR
 * - The admin has configured showAllAppsAccessMode as SecurityGroup AND the user is a member of that group
 *
 * Unlike useShowEntraIdLinks this fails closed: while anything is loading, or if anything fails,
 * the switch stays disabled. It is a UI gate only; what the user can actually read is still decided
 * by Entra through their own delegated Graph token.
 *
 * @returns true if the switch should be enabled, false otherwise
 */
const useCanShowAllApps = (): boolean => {
  const { data: appEnabledInfo } = useQuery({
    ...appQueries.isAppEnabled,
    // Cache for 5 minutes since this is a global setting that rarely changes
    staleTime: 5 * 60 * 1000,
  });

  const { data: adminInfo } = useQuery({
    ...graphQueries.isAppMgmtAdmin,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  });

  const settings = appEnabledInfo?.settings;
  const groupId =
    settings?.showAllAppsAccessMode === ShowAllAppsAccessMode.SecurityGroup
      ? settings.showAllAppsSecurityGroupId
      : undefined;

  const { data: isMember } = useQuery({
    ...graphQueries.isMemberOfGroup(groupId ?? ''),
    // Only a non-admin in SecurityGroup mode needs the membership, and it is checked once per session
    enabled: !!groupId && adminInfo?.isAdmin === false,
    staleTime: Infinity,
  });

  if (adminInfo?.isAdmin) return true;
  if (!settings || !adminInfo) return false;

  switch (settings.showAllAppsAccessMode) {
    case ShowAllAppsAccessMode.AllUsers:
      return true;
    case ShowAllAppsAccessMode.SecurityGroup:
      return isMember === true;
    default:
      return false;
  }
};

export default useCanShowAllApps;
